import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/Logo";
import { BookOpen, Download } from "lucide-react";

const DOCS = [
  { key: "guide", label: "User Guide", file: "/USER_GUIDE.md" },
];

function useMarkdown(path) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!path) return;
    fetch(path).then((r) => r.text()).then(setText).catch(() => setText("Failed to load."));
  }, [path]);
  return text;
}

// Extremely small markdown-to-JSX for headings, lists, tables, code blocks, and paragraphs.
function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  const key = () => `n-${out.length}`;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) { buf.push(lines[i]); i++; }
      i++;
      out.push(
        <pre key={key()} className="border-2 border-border bg-muted p-3 overflow-x-auto text-xs font-mono whitespace-pre">{buf.join("\n")}</pre>
      );
      continue;
    }
    if (line.startsWith("### ")) { out.push(<h3 key={key()} className="font-heading text-xl font-black tracking-tight mt-6 mb-2">{line.slice(4)}</h3>); i++; continue; }
    if (line.startsWith("## "))  { out.push(<h2 key={key()} className="font-heading text-2xl font-black tracking-tighter mt-8 mb-3 border-b-2 border-border pb-2">{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith("# "))   { out.push(<h1 key={key()} className="font-heading text-4xl font-black tracking-tighter mb-6">{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith("> ")) { out.push(<blockquote key={key()} className="border-l-4 border-orange-600 bg-orange-50 dark:bg-orange-950 px-4 py-3 text-sm my-3">{inline(line.slice(2))}</blockquote>); i++; continue; }
    if (line.startsWith("|") && lines[i + 1]?.match(/^\|[\s-|:]+\|$/)) {
      const header = line.split("|").slice(1, -1).map((s) => s.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").slice(1, -1).map((s) => s.trim()));
        i++;
      }
      out.push(
        <div key={key()} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-2 border-border">
            <thead className="bg-muted">
              <tr>{header.map((h, k) => <th key={k} className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold">{inline(h)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, k) => <tr key={k} className="border-t border-border">{r.map((c, m) => <td key={m} className="px-3 py-2 align-top">{inline(c)}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      out.push(<ul key={key()} className="list-disc pl-6 my-2 space-y-1 text-sm">{items.map((t, k) => <li key={k}>{inline(t)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push(<ol key={key()} className="list-decimal pl-6 my-2 space-y-1 text-sm">{items.map((t, k) => <li key={k}>{inline(t)}</li>)}</ol>);
      continue;
    }
    if (line.trim() === "") { i++; continue; }
    out.push(<p key={key()} className="text-sm leading-relaxed my-2">{inline(line)}</p>);
    i++;
  }
  return out;
}

function inline(s) {
  const parts = [];
  let rest = s;
  let idx = 0;
  const patterns = [
    { re: /\*\*(.+?)\*\*/, wrap: (m) => <strong key={idx++}>{m}</strong> },
    { re: /`([^`]+)`/, wrap: (m) => <code key={idx++} className="font-mono text-[13px] bg-muted px-1 py-0.5">{m}</code> },
  ];
  while (rest.length) {
    let matched = false;
    for (const { re, wrap } of patterns) {
      const m = rest.match(re);
      if (m && m.index !== undefined) {
        if (m.index > 0) parts.push(rest.slice(0, m.index));
        parts.push(wrap(m[1]));
        rest = rest.slice(m.index + m[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) { parts.push(rest); break; }
  }
  return parts;
}

export default function Docs() {
  const [tab, setTab] = useState("guide");
  const active = DOCS.find((d) => d.key === tab);
  const md = useMarkdown(active?.file);

  const download = (file, name) => {
    const a = document.createElement("a");
    a.href = file;
    a.download = name;
    a.click();
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2">
          <LogoMark size={40} className="text-foreground" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In &middot; Instruction Pack
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Docs</h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="rounded-none bg-transparent border-b-2 border-border w-full sm:w-auto justify-start p-0 h-auto">
            {DOCS.map((d) => (
              <TabsTrigger
                key={d.key}
                value={d.key}
                data-testid={`docs-tab-${d.key}`}
                className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold"
              >
                {d.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <Button
            variant="outline"
            size="sm"
            onClick={() => download(active.file, active.file.slice(1))}
            data-testid="docs-download"
            className="rounded-none border-2 h-9 text-xs uppercase tracking-[0.15em] font-bold"
          >
            <Download className="h-3.5 w-3.5 mr-2" /> Download .md
          </Button>
        </div>

        {DOCS.map((d) => (
          <TabsContent key={d.key} value={d.key} className="mt-4">
            <article data-testid={`docs-content-${d.key}`} className="border-2 border-border bg-card p-4 sm:p-8">
              {renderMarkdown(md)}
            </article>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
